import axios, { AxiosError } from 'axios';
import { supabase } from './supabase';
import { appConfig } from '../config/appConfig';

const baseURL = appConfig.apiUrl;

export const api = axios.create({
  baseURL,
  timeout: 30000,
});

// Interceptor: agregar Bearer Token de Supabase en cada request
api.interceptors.request.use(async (config) => {
  const { data } = await supabase.auth.getSession();
  if (data.session?.access_token) {
    config.headers.Authorization = `Bearer ${data.session.access_token}`;
  }
  return config;
});

/**
 * Interceptor de respuestas — manejo de errores 401.
 *
 * Diseño defensivo (post-fix Fase 8):
 *
 * El interceptor anterior cerraba la sesión global y redirigía al login
 * ante CUALQUIER 401, lo que generaba un "flash de login → dashboard"
 * confuso cuando un endpoint puntual fallaba con 401 (por ejemplo,
 * permisos mal configurados, JWT desactualizado pero refreshable, etc.).
 *
 * Nueva lógica:
 *   1. Si la respuesta es 401, primero verificamos con Supabase si la
 *      sesión local sigue siendo válida.
 *   2. Si la sesión NO existe → el usuario realmente perdió el acceso →
 *      lo mandamos al login (comportamiento anterior).
 *   3. Si la sesión SÍ existe → es un problema puntual del endpoint:
 *      permisos, rol incorrecto, perfil deshabilitado, etc. Dejamos
 *      pasar el error y el componente que llamó al endpoint lo maneja
 *      (típicamente con un Alert "Error al cargar").
 *
 * Esto evita el bug donde un 401 puntual te deslogueaba completamente.
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      try {
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          // Sesión genuinamente expirada / inexistente → al login.
          window.location.href = '/auth/login';
          return Promise.reject(error);
        }
      } catch {
        // Si ni siquiera podemos consultar la sesión, asumimos que el
        // estado del cliente está roto → al login como fallback seguro.
        window.location.href = '/auth/login';
        return Promise.reject(error);
      }
      // Sesión existe pero el endpoint específico devolvió 401.
      // No desloguear globalmente — dejar que el componente maneje el
      // error. Logueamos para debug.
      // eslint-disable-next-line no-console
      console.warn(
        `[api] 401 en ${error.config?.method?.toUpperCase()} ${error.config?.url} ` +
          `pero la sesión sigue activa. El endpoint denegó acceso puntual ` +
          `(¿permisos? ¿rol?). El error se propaga al caller.`,
      );
    }
    return Promise.reject(error);
  },
);
